import { useMemo, useState } from 'react';

import { Icon } from '../../../components/Icon';
import { Modal } from '../../../components/ui';
import { AuthenticatedImage } from '../../../shared/components/AuthenticatedImage';
import { AuthenticatedPdf } from '../../../shared/components/AuthenticatedPdf';
import type { HelpArticle, HelpArticleDetail } from '../../../types/helpArticles';
import styles from './MobileBlogPreview.module.scss';

type MobileBlogPreviewProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly subtitle: string;
  readonly closeLabel: string;
  readonly articles: readonly HelpArticle[];
  readonly screen: 'list' | 'article';
  readonly article: HelpArticleDetail | null;
  readonly articleLoading: boolean;
  readonly pendingHeroFile?: File | null;
  readonly onOpenArticle: (articleId: string) => void;
  readonly onShowList: () => void;
};

const ukrainianMonths = [
  'січня',
  'лютого',
  'березня',
  'квітня',
  'травня',
  'червня',
  'липня',
  'серпня',
  'вересня',
  'жовтня',
  'листопада',
  'грудня',
] as const;

export function MobileBlogPreview({
  open,
  onClose,
  title,
  subtitle,
  closeLabel,
  articles,
  screen,
  article,
  articleLoading,
  pendingHeroFile = null,
  onOpenArticle,
  onShowList,
}: MobileBlogPreviewProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      closeAriaLabel={closeLabel}
      size="xl"
    >
      <div className={styles.previewStage}>
        <div className={styles.phone} aria-label={title}>
          <div className={styles.speaker} aria-hidden="true" />
          <div className={styles.mobileApp}>
            {screen === 'list' ? (
              <MobileBlogList articles={articles} onOpenArticle={onOpenArticle} />
            ) : (
              <MobileArticleDetail
                article={article}
                loading={articleLoading}
                pendingHeroFile={pendingHeroFile}
                onBack={onShowList}
              />
            )}
            <MobileBottomNavigation />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function MobileBlogList({
  articles,
  onOpenArticle,
}: {
  readonly articles: readonly HelpArticle[];
  readonly onOpenArticle: (articleId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const filteredArticles = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('uk');
    if (!normalized) return articles;
    return articles.filter(
      (article) =>
        article.title.toLocaleLowerCase('uk').includes(normalized) ||
        article.summary.toLocaleLowerCase('uk').includes(normalized),
    );
  }, [articles, query]);

  return (
    <div className={styles.mobileScreen}>
      <div className={styles.mobileHeader}>
        <h4>Допомога</h4>
        <span aria-hidden="true" className={styles.roundIcon}>
          <Icon name="inbox" size={17} />
        </span>
        <span aria-hidden="true" className={styles.roundIcon}>
          <Icon name="bell" size={17} />
        </span>
      </div>

      <label className={styles.mobileSearch}>
        <span className={styles.visuallyHidden}>Пошук статей</span>
        <Icon name="search" size={19} />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Пошук"
        />
      </label>

      <div className={styles.mobileArticleList}>
        {filteredArticles.length === 0 ? (
          <p className={styles.mobileEmpty}>Статті не знайдено</p>
        ) : (
          filteredArticles.map((article) => (
            <MobileArticleCard
              key={article.id}
              article={article}
              onClick={() => onOpenArticle(article.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MobileArticleCard({ article, onClick }: { readonly article: HelpArticle; readonly onClick: () => void }) {
  const isVideo = article.hero_kind === 'video_upload' || article.hero_kind === 'video_youtube';
  const previewImagePath = article.preview_image_url?.trim() || article.hero_image_url;

  return (
    <button type="button" className={styles.mobileArticleCard} onClick={onClick} aria-label={article.title}>
      {previewImagePath ? (
        <div className={styles.cardHero}>
          <AuthenticatedImage
            path={previewImagePath}
            alt=""
            fallback={<span className={styles.imageSkeleton} />}
            style={{
              objectFit: article.preview_fit,
              objectPosition: `${article.preview_position_x}% ${article.preview_position_y}%`,
            }}
          />
          {isVideo ? <PlayOverlay /> : null}
        </div>
      ) : null}
      <div className={styles.cardBody}>
        <div className={styles.cardCopy}>
          <h5>{article.title}</h5>
          <p>{article.summary}</p>
        </div>
        <span className={styles.cardArrow} aria-hidden="true">↗</span>
      </div>
    </button>
  );
}

function MobileArticleDetail({
  article,
  loading,
  pendingHeroFile,
  onBack,
}: {
  readonly article: HelpArticleDetail | null;
  readonly loading: boolean;
  readonly pendingHeroFile: File | null;
  readonly onBack: () => void;
}) {
  if (loading || !article) {
    return (
      <div className={styles.mobileScreen}>
        <MobileDetailHeader title="" onBack={onBack} />
        <div className={styles.articleLoading} aria-label="Завантаження статті" />
      </div>
    );
  }

  const isVideo = article.hero_kind === 'video_upload' || article.hero_kind === 'video_youtube';
  const showPdf = article.hero_kind === 'pdf' && article.hero_pdf_url && !pendingHeroFile;

  return (
    <div className={styles.mobileScreen}>
      <MobileDetailHeader title={article.title} onBack={onBack} />
      {showPdf ? (
        <div className={styles.mobilePdf}>
          <AuthenticatedPdf path={article.hero_pdf_url!} title={article.title} />
        </div>
      ) : (
        <div className={styles.articleContent}>
          {pendingHeroFile || article.hero_image_url ? (
            <div className={styles.detailHero}>
              <AuthenticatedPreviewImage
                path={pendingHeroFile ?? article.hero_image_url!}
                alt=""
              />
              {isVideo ? <PlayOverlay /> : null}
            </div>
          ) : null}
          {article.published_at ? (
            <p className={styles.publishedDate}>Опубліковано: {formatPublishedDate(article.published_at)}</p>
          ) : null}
          <div className={styles.articleSections}>
            {article.sections.map((section, index) => (
              <section key={`${index}-${section.heading}`}>
                <h5>{section.heading}</h5>
                <p>{section.body}</p>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileDetailHeader({ title, onBack }: { readonly title: string; readonly onBack: () => void }) {
  return (
    <div className={styles.detailHeader}>
      <button type="button" onClick={onBack} aria-label="Назад до списку статей">
        <Icon name="arrow-left" size={18} />
      </button>
      <h4>{title}</h4>
      <span aria-hidden="true" />
    </div>
  );
}

function PlayOverlay() {
  return (
    <span className={styles.playOverlay} aria-hidden="true">
      <Icon name="play" size={31} />
    </span>
  );
}

function MobileBottomNavigation() {
  return (
    <div className={styles.bottomNavigation} aria-hidden="true">
      <span><Icon name="dashboard" size={17} /></span>
      <span><Icon name="inbox" size={17} /></span>
      <span><Icon name="payment" size={17} /></span>
      <span className={styles.activeNav}><Icon name="info" size={17} />Допомога</span>
    </div>
  );
}

function AuthenticatedPreviewImage({ path, alt }: { readonly path: string | File; readonly alt: string }) {
  return (
    <AuthenticatedImage
      path={typeof path === 'string' ? path : null}
      file={path instanceof File ? path : null}
      alt={alt}
      fallback={<span className={styles.imageSkeleton} />}
    />
  );
}

function formatPublishedDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getDate()} ${ukrainianMonths[date.getMonth()]} ${date.getFullYear()}`;
}
